import { z } from "zod";

/**
 * Validated, typed access to build-time environment variables. Fails fast and loudly
 * at module load if a required `VITE_*` value is missing or malformed, rather than
 * letting the app limp along with `undefined` sprinkled through API calls.
 */
const envSchema = z.object({
  VITE_CDP_PROJECT_ID: z.string().min(1, "VITE_CDP_PROJECT_ID is required"),
  VITE_BASE_CHAIN_ID: z.coerce
    .number()
    .int()
    .positive("VITE_BASE_CHAIN_ID must be a positive integer"),
  VITE_API_PUBLIC_URL: z.string().url("VITE_API_PUBLIC_URL must be a valid URL"),
  // Optional: error tracking. Sentry.init() no-ops safely when the dsn is undefined, so this
  // is fine to leave unset in local dev.
  VITE_SENTRY_DSN: z.string().url().optional(),
  // Optional: X (Twitter) Ads conversion tracking — see lib/xPixel.ts. All three no-op safely
  // when unset. VITE_X_PIXEL_ID is the base Website Tag pixel ID; the two event ids (e.g.
  // "tw-xxxxx-yyyyy") come verbatim from the snippets X generates for each conversion event
  // created in Ads Manager's Events Manager.
  VITE_X_PIXEL_ID: z.string().min(1).optional(),
  VITE_X_PAGEVIEW_EVENT_ID: z.string().min(1).optional(),
  VITE_X_SIGNUP_EVENT_ID: z.string().min(1).optional(),
  // Optional: Google Analytics 4 — see lib/ga4.ts. No-ops safely when unset. The GA4
  // "Measurement ID" (e.g. "G-XXXXXXXXXX") from the property's Data Streams settings.
  VITE_GA4_MEASUREMENT_ID: z.string().min(1).optional(),
  // Optional: Base Builder Code (e.g. "bc_xxxxxxxx") appended to every transaction as an ERC-8021
  // attribution suffix — see lib/builderCode.ts. Unset means no attribution suffix is added.
  VITE_BASE_BUILDER_CODE: z
    .string()
    .regex(/^bc_[a-z0-9]+$/, "VITE_BASE_BUILDER_CODE must look like bc_xxxxxxxx")
    .optional(),
  // Optional: "true" asks Coinbase's Paymaster to sponsor gas on user operations. Requires the
  // Paymaster to be configured in the CDP Portal first — see lib/coinbase/sponsorship.ts.
  VITE_USE_CDP_PAYMASTER: z.enum(["true", "false"]).optional(),
});

function loadEnv() {
  // A build arg that is declared but blank (Docker Compose passes `VAR: ""` for an unset
  // `${VAR:-}`) reaches Vite as an empty string, not as undefined. Treat blank as unset so the
  // optional values above can be left empty per deployment.
  const source = Object.fromEntries(
    Object.entries(import.meta.env).filter(([, value]) => value !== ""),
  );
  const result = envSchema.safeParse(source);
  if (!result.success) {
    const issues = result.error.issues.map(
      (issue) => `- ${issue.path.join(".")}: ${issue.message}`,
    );
    throw new Error(
      `Invalid frontend environment configuration.\n${issues.join("\n")}\n` +
        "Check apps/web's Vite environment (.env.local or the Railway service variables).",
    );
  }
  return result.data;
}

export const env = loadEnv();

/** Strips a trailing slash so callers can safely do `${apiBaseUrl}/api/v1/...`. */
export const apiBaseUrl = env.VITE_API_PUBLIC_URL.replace(/\/+$/, "");
